import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { UserProfile } from '../types';
import { Loader, Save } from 'lucide-react';

export default function ProfilePage() {
  const { profile, updateProfile, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserProfile>({
    name: '',
    role: '',
    companyDescription: '',
    companyPainPoints: '',
    companyUrl: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      setIsLoading(false);
      if (profile) {
        setFormData({
          name: profile.name || '',
          role: profile.role || '',
          companyDescription: profile.company_description || '',
          companyPainPoints: profile.company_pain_points || '',
          companyUrl: profile.company_url || ''
        });
      }
    }
  }, [profile, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      await updateProfile({
        name: formData.name,
        role: formData.role,
        company_description: formData.companyDescription,
        company_pain_points: formData.companyPainPoints,
        company_url: formData.companyUrl
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Complete Your Profile</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Help us understand your role and company to generate better responses
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
          <div className="bg-white/80 rounded-xl p-6 space-y-8">
            {/* Personal Information */}
            <div className="space-y-4">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
                  <div className="text-xl font-bold text-blue-600">1</div>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
                  <p className="text-sm text-gray-600">Tell us about yourself</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Role
                  </label>
                  <input
                    type="text"
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="space-y-4 pt-4">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center mr-3">
                  <div className="text-xl font-bold text-blue-600">2</div>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Company Information</h2>
                  <p className="text-sm text-gray-600">Tell us about your company</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="companyUrl" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Website
                  </label>
                  <input
                    type="url"
                    id="companyUrl"
                    value={formData.companyUrl}
                    onChange={(e) => setFormData({ ...formData, companyUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="companyDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    What does your company do?
                  </label>
                  <textarea
                    id="companyDescription"
                    value={formData.companyDescription}
                    onChange={(e) => setFormData({ ...formData, companyDescription: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="companyPainPoints" className="block text-sm font-medium text-gray-700 mb-1">
                    What pain points does your company solve for customers?
                  </label>
                  <textarea
                    id="companyPainPoints"
                    value={formData.companyPainPoints}
                    onChange={(e) => setFormData({ ...formData, companyPainPoints: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save'}
            {!isSaving && <Save className="ml-2 w-5 h-5" />}
          </button>
        </div>
      </form>
    </div>
  );
}